/**
 * Refund-verdict logic. Single source of truth for the rules in
 * `apps/landing/src/pages/refund.astro`. Used by both the server-side admin
 * stats endpoint AND the local CLI (scripts/admin-user-stats.mjs mirrors
 * this — keep them in sync; tests pin the contract).
 *
 * Policy (changed 2026-05-13):
 *   - In free trial         → no charge happened, just cancel
 *   - Never charged         → nothing to refund
 *   - Last charge ≤ 48 h    → eligible for full refund, no questions
 *   - Last charge > 48 h    → not eligible by default rules
 *
 * The single 48-hour window applies to BOTH the first charge (when
 * the 7-day trial ends) and to every subsequent renewal. The old
 * 14-day post-trial window is gone — Tornike collapsed the policy
 * to one uniform rule for clarity.
 */
export type RefundEligibility =
  | 'no_subscription'
  | 'never_charged'
  | 'in_trial'
  | 'eligible_48h'
  | 'not_eligible';

export interface RefundVerdict {
  tag: RefundEligibility;
  reason: string;
}

export interface RefundInputs {
  /** Latest subscription row (or null). */
  subscription: {
    status: 'trialing' | 'active' | 'past_due' | 'paused' | 'canceled';
    trial_ends_at: string | null;
    current_period_starts_at: string | null;
  } | null;
  /** ISO timestamp of the FIRST `transaction.completed` for this subscription (null if never). */
  firstChargeAt: string | null;
  /** Defaults to `Date.now()`; injectable for tests. */
  now?: number;
}

const HOUR_MS = 3_600_000;

export function refundVerdict(input: RefundInputs): RefundVerdict {
  const now = input.now ?? Date.now();
  const sub = input.subscription;

  if (!sub) {
    return { tag: 'no_subscription', reason: 'No subscription on file. Nothing to refund.' };
  }

  const trialEnd = sub.trial_ends_at ? new Date(sub.trial_ends_at).getTime() : null;
  if (sub.status === 'trialing' && trialEnd && now < trialEnd) {
    return {
      tag: 'in_trial',
      reason:
        'User is in the 7-day free trial — no charge has happened yet. ' +
        'Nothing to refund. They can cancel themselves on the Profile screen.',
    };
  }

  if (!input.firstChargeAt) {
    return {
      tag: 'never_charged',
      reason:
        'No transaction.completed event recorded for this subscription. Nothing to refund.',
    };
  }

  // "Last charge" = the more recent of (first charge, current period start).
  // On the first paid period these are equal. On renewals, period_start is
  // newer. Falls back to first_charge if period_start is null.
  const firstChargeMs = new Date(input.firstChargeAt).getTime();
  const periodStartMs = sub.current_period_starts_at
    ? new Date(sub.current_period_starts_at).getTime()
    : null;
  const lastChargeMs =
    periodStartMs !== null && periodStartMs > firstChargeMs ? periodStartMs : firstChargeMs;
  const hoursSinceLastCharge = (now - lastChargeMs) / HOUR_MS;

  if (hoursSinceLastCharge <= 48) {
    return {
      tag: 'eligible_48h',
      reason: `Last charge was ${hoursSinceLastCharge.toFixed(1)} hours ago — within the 48-hour no-questions-asked window. Issue full refund in Paddle.`,
    };
  }

  return {
    tag: 'not_eligible',
    reason:
      `Outside the 48-hour refund window (last charge ${hoursSinceLastCharge.toFixed(1)}h ago). ` +
      `Tell them they can cancel in Paddle to stop next charge. ` +
      `If they're claiming >24h outage, verify against Cloudflare/Supabase status, then issue prorated credit manually.`,
  };
}
